import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiSlash, FiPlus, FiSearch, FiFilter, FiTrash2, FiGlobe, FiSmartphone, FiClock, FiX, FiCheck } = FiIcons;
import { mockSuppressions, mockApps, mockProviders } from '../data/mockData';

const AddSuppressionModal = ({ onSave, onClose }) => {
  const [formData, setFormData] = useState({ email: '', reason: 'Manual Block', scope: 'Global', notes: '' });

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2">
            <SafeIcon icon={FiPlus} className="text-blue-400" /> Add to Suppression List
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded"><SafeIcon icon={FiX} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Email Address</label>
            <input type="email" placeholder="example@bad-domain.com" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500 transition-all font-medium" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Reason</label>
              <select className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})}>
                <option>Bounced</option>
                <option>Unsubscribed</option>
                <option>Complaint</option>
                <option>Invalid email</option>
                <option>Manual block</option>
                <option>Abuse prevention</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Scope</label>
              <select className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.scope} onChange={(e) => setFormData({...formData, scope: e.target.value})}>
                <option>Global</option>
                <option>App-specific</option>
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Internal Notes</label>
            <textarea className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm h-20 resize-none" placeholder="Reason for this block..." value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
          </div>
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2 text-sm font-bold text-slate-500 hover:text-slate-700">Cancel</button>
          <button onClick={() => onSave(formData)} className="px-8 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-blue-700 transition-all">Add Suppression</button>
        </div>
      </div>
    </div>
  );
};

const SuppressionList = () => {
  const [suppressions, setSuppressions] = useState(mockSuppressions);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeModal, setActiveModal] = useState(false);

  const deleteSuppression = (id) => {
    if (confirm('Are you sure you want to remove this email from the suppression list?')) {
      setSuppressions(suppressions.filter(s => s.id !== id));
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Suppression List</h2>
          <p className="text-sm text-slate-500">Blocked recipients managed globally or per application.</p>
        </div>
        <button onClick={() => setActiveModal(true)} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center gap-2">
          <SafeIcon icon={FiPlus} /> Add Email
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex-1 flex items-center px-3 gap-2">
          <SafeIcon icon={FiSearch} className="text-slate-400" />
          <input type="text" placeholder="Search emails or reasons..." className="w-full text-sm outline-none bg-transparent" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase text-slate-500 font-bold border-b border-slate-100">
                <th className="px-6 py-4">Suppressed Email</th>
                <th className="px-6 py-4">Reason</th>
                <th className="px-6 py-4">Scope / Source</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Date Added</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-slate-50">
              {suppressions.map(item => (
                <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 font-bold text-slate-800">{item.email}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${item.reason === 'Bounced' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                      {item.reason}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-xs font-bold text-slate-700">{item.scope}</p>
                    <p className="text-[10px] text-slate-400 italic">Source: {item.source}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${item.status === 'active' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[11px] text-slate-400 font-mono">{item.date}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => deleteSuppression(item.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-4 shadow-sm">
        <SafeIcon icon={FiIcons.FiInfo} className="text-blue-500 text-xl shrink-0 mt-0.5" />
        <p className="text-xs text-blue-800 leading-relaxed">
          <b>Staff Note:</b> Real-time suppression checks happen at the gateway ingress layer. If a recipient is on this list, the gateway will return a <code>403 Suppressed</code> error without attempting to send.
        </p>
      </div>

      {activeModal && (
        <AddSuppressionModal 
          onClose={() => setActiveModal(false)}
          onSave={(data) => {
            setSuppressions([{ ...data, id: `sup_${Date.now()}`, date: 'Today', source: 'Admin Portal', status: 'active' }, ...suppressions]);
            setActiveModal(false);
          }}
        />
      )}
    </div>
  );
};

export default SuppressionList;